/**
 * The capital stack and its leverage, read once for whichever surface shows
 * them — the Overview's Leverage & Capitalization card and the Capital Stack
 * tab — so the two can never disagree about the same deal (Briar Hollow once
 * read 100% senior on one and 60/40 on the other).
 *
 * Values come through the dossier's own reader (`effectiveValue`: the saved
 * deal, the intake overlay and any unsaved edit), the engine's derived value
 * for a field the deal leaves empty (the calculations service — the same
 * cached query Economics makes), and the field lineage for each figure's
 * state and whose statement it is.
 *
 * The loan structure is read here too, under the Capital Stack tab's own
 * query key, when a caller does not hand it in. Its interest and capex
 * reserves are uses, so they decide the residual equity and the LTC basis;
 * the Overview card passed none and, on the same deal, read no equity and a
 * 75.0% LTC beside the tab's $10M equity and 68.2%.
 */
import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { holdsSponsorFigure, sponsorFigure, type RawDealFile } from '@/services/dealFile'
import type { DealDocument } from '@/services/documents'
import { structuresService, type LoanStructure } from '@/services/structures'
import type { LoanActivityEvent, LoanFieldLineageField } from '@/pages/pipeline-intelligence/types'
import { loansService } from '@/services/loans'
import { LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'
import { stateOfLineage } from '@/components/fields/lineage'
import { useDossierEdit } from './editContext'
import { buildCapitalStackModel } from './capitalStackModel'
import { buildLeverage, calculatedFrom } from './leverageModel'
import { lineageSource } from './valueStateChip'
import type { FieldSourceRef } from './workspace/types'

const NO_DOCUMENTS: DealDocument[] = []
const NO_ACTIVITY: LoanActivityEvent[] = []
const NO_NOTES: { id: string; body: string; author: string; createdAt: string }[] = []
const NO_LINEAGE: LoanFieldLineageField[] = []

/** Percent fields keep 0–100 unless a native column holds them; `ltv` is the
 *  only native percent, and it stores a fraction (see holdsSponsorFigure). */
const SPONSOR_SCALE = { native: false, kind: 'number' } as const
const NATIVE_PERCENT = { native: true, kind: 'percent' } as const

export function useCapitalization({
  raw,
  loanId,
  structure: structureGiven,
  documents = NO_DOCUMENTS,
  activity = NO_ACTIVITY,
  notes = NO_NOTES,
  lineage = NO_LINEAGE,
}: {
  raw: RawDealFile | undefined
  loanId: string
  /** The loan structure, when the caller already reads it; read here (same
   *  key, same cache) when left out, so no surface builds without it. */
  structure?: LoanStructure | null
  documents?: DealDocument[]
  activity?: LoanActivityEvent[]
  notes?: { id: string; body: string; author: string; createdAt: string }[]
  lineage?: LoanFieldLineageField[]
}) {
  const { effectiveValue, isDerivedField, derivation } = useDossierEdit()
  const calculations = useQuery({
    queryKey: LOAN_QUERY_KEYS.calculations(loanId),
    queryFn: () => loansService.getCalculations(loanId),
    enabled: Boolean(loanId),
    retry: false,
  })
  // The Capital Stack tab's key (CapitalStackPage), so both read one cache.
  const structureQuery = useQuery({
    queryKey: ['loan', loanId, 'structure'],
    queryFn: () => structuresService.get(loanId),
    enabled: Boolean(loanId) && structureGiven === undefined,
    retry: false,
  })
  const structure = structureGiven !== undefined ? structureGiven : structureQuery.data ?? null
  const get = useCallback((name: string) => effectiveValue(name), [effectiveValue])
  const lineageOf = useCallback(
    (name: string) => lineage.find((field) => field.fieldName === name),
    [lineage],
  )

  const equityLineage = lineageOf('total_equity')
  const equityIsDerived = Boolean(equityLineage && stateOfLineage(equityLineage) === 'derived')
  const equityFormula = equityLineage?.derivation?.formula ?? null
  const equityDerivation = useMemo(
    () => (equityIsDerived ? { formula: equityFormula } : null),
    [equityIsDerived, equityFormula],
  )
  const propertyValueDerived = stateOfLineage(lineageOf('property_value')) === 'derived'

  const stack = useMemo(
    () =>
      buildCapitalStackModel({
        get,
        raw,
        structure,
        documents,
        activity,
        notes,
        equityDerivation,
        propertyValueDerived,
      }),
    [get, raw, structure, documents, activity, notes, equityDerivation, propertyValueDerived],
  )
  const calculated = useMemo(() => calculatedFrom(calculations.data?.derived), [calculations.data])
  // A value the field holds that is itself computed: the dossier's derivation
  // preview (what the engine recalculates from the deal's own figures), or a
  // field whose lineage says Derived. Its formula, or null for a stated one.
  const heldDerived = useCallback(
    (name: string): string | null => {
      if (isDerivedField(name)) return derivation?.notes?.[name] ?? 'recalculated from the deal’s own figures'
      const row = lineageOf(name)
      if (row && stateOfLineage(row) === 'derived') return row.derivation?.formula ?? 'computed from other fields'
      return null
    },
    [derivation, isDerivedField, lineageOf],
  )
  const leverage = useMemo(
    () => buildLeverage({ get, calculated, heldDerived, stack }),
    [get, calculated, heldDerived, stack],
  )

  /**
   * A catalogue figure's value state and whose statement it is, for the chip
   * and the Sponsor mark beside it. The lineage decides (a person's
   * confirmation, the engine's derivation, the sponsor's cell); with no
   * lineage row, a value that equals the sponsor model's own cell is the
   * sponsor's — the same test the metric bar applies.
   */
  const sourceOf = useCallback(
    (name: string | undefined): FieldSourceRef | null => {
      if (!name) return null
      const fromLineage = lineageSource(name, lineageOf(name))
      if (fromLineage) return fromLineage
      const figure = sponsorFigure(raw, name)
      if (figure && holdsSponsorFigure(name === 'ltv' ? NATIVE_PERCENT : SPONSOR_SCALE, get(name), figure)) {
        return {
          label: `${figure.filename} ${figure.sheet}!${figure.cell}`,
          detail: null,
          fieldName: name,
          attribution: 'sponsor',
          cellRef: `${figure.sheet}!${figure.cell}`,
          disputed: false,
          state: 'stated',
        }
      }
      return null
    },
    [get, lineageOf, raw],
  )

  return { stack, leverage, sourceOf, get }
}

export type CapitalizationRead = ReturnType<typeof useCapitalization>
